import { Injectable } from '@angular/core';
import PouchDB from 'pouchdb';
import { environment } from '../../environments/environment';
import { BehaviorSubject, combineLatest, from, map, Observable, of } from 'rxjs';
import { FormResponseDoc } from '../forms/forms.service';

@Injectable({
  providedIn: 'root',
})
export class CouchSyncService {
  private localDatabases = new Map<string, PouchDB.Database>();
  private globalClientDb = new PouchDB('clients');
  private syncHandlers = new Map<string, PouchDB.Replication.Replication<{}>>();
  private globalLoadingSubject = new BehaviorSubject<boolean>(false);
  private dbStates = new Map<string, BehaviorSubject<boolean>>();
  public isGlobalLoading$: Observable<boolean> = of(false);
  public startFullSync(
    session: { username: string; uuid: string; sectors: string[] },
    password: string,
  ) {
    this.cleanUpObsoleteDatabases(session.sectors);

    const dbs = [
      'employee',
      'forms',
      'clients',
      'causerie_thematiques',
      'sous_traitants',
      'epis_equipements',
      ...session.sectors,
    ];

    this.initLoadingState(dbs);

    this.globalLoadingSubject.next(true);
    dbs.forEach((db) => this.initSectorSync(db, session.username, password));
    this.initSync('user_' + session.uuid, session.username, password);

    this.getResponseToDelete(session.uuid).subscribe({
      next: (res) => {
        res.map((r) => new PouchDB('user_' + session.uuid).remove(r as any));
      },
    });

    this.isGlobalLoading$ = combineLatest(
      dbs.map((name) => this.dbStates.get(name)!.asObservable()),
    ).pipe(map((states) => states.some((isLoading) => isLoading === true)));

    if (!session.sectors || !Array.isArray(session.sectors)) return;

    session.sectors.forEach((sector: any) => {
      this.initSectorSync(sector, session.username, password);
      this.mergeSectorToGlobal(sector);
    });

    combineLatest(dbs.map((name) => this.dbStates.get(name)!.asObservable()))
      .pipe(map((states) => states.some((isLoading) => isLoading === true)))
      .subscribe((isLoading) => {
        this.globalLoadingSubject.next(isLoading);
      });
  }

  private getResponseToDelete(userUuid: string): Observable<FormResponseDoc[]> {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const isoBoundaryDate = sevenDaysAgo.toISOString();
    const promise = new PouchDB('user_' + userUuid)
      .find({
        selector: {
          type: 'response',
          status: 'draft',
          createdAt: { $lt: isoBoundaryDate },
        },
      })
      .then((res) => {
        return res.docs as unknown as FormResponseDoc[];
      });

    return from(promise);
  }

  private initLoadingState(dbs: string[]) {
    dbs.forEach((name) => {
      this.dbStates.set(name, new BehaviorSubject<boolean>(true));
    });
  }

  private setDbLoading(dbName: string, isLoading: boolean) {
    const state = this.dbStates.get(dbName);
    if (state && state.value !== isLoading) {
      state.next(isLoading);
    }
  }

  private initSectorSync(dbName: string, couchUser: string, pass: string) {
    if (this.localDatabases.has(dbName)) return;
    const localDb = new PouchDB(dbName);
    const remoteUrl = `${environment.couchUrl}/${dbName}`;
    const remoteDb = new PouchDB(remoteUrl, {
      auth: {
        username: couchUser,
        password: pass,
      },
    });
    const sync = localDb.replicate
      .from(remoteDb, {
        since: 0,
        live: dbName.includes('user_'),
        retry: false,
      })
      .on('active', () => this.setDbLoading(dbName, true))
      .on('paused', () => this.setDbLoading(dbName, false))
      .on('complete', () => this.setDbLoading(dbName, false))
      .on('change', (info) => console.log(`sync: Change [${dbName}]:`, info))
      .on('error', (err) => {
        this.setDbLoading(dbName, false);
        console.error(`sync: Error [${dbName}]:`, err);
      })
      .on('denied', (err) => {
        this.setDbLoading(dbName, false);
        console.error(err);
      });
    this.localDatabases.set(dbName, localDb);
    this.syncHandlers.set(dbName, sync);
  }

  private initSync(dbName: string, couchUser: string, pass: string) {
    if (this.localDatabases.has(dbName)) return;
    const localDb = new PouchDB(dbName);
    const remoteUrl = `${environment.couchUrl}/${dbName}`;
    const remoteDb = new PouchDB(remoteUrl, {
      auth: {
        username: couchUser,
        password: pass,
      },
    });
    localDb
      .sync(remoteDb, {
        since: 0,
        live: true,
        retry: false,
      })
      .on('change', (info) => console.log(`sync: Change [${dbName}]:`, info))
      .on('error', (err) => console.error(`sync: Error [${dbName}]:`, err))
      .on('denied', (err) => console.error(err));
    this.localDatabases.set(dbName, localDb);
  }

  async cleanUpObsoleteDatabases(apiDatabases: string[]) {
    try {
      const idbDatabases = await indexedDB.databases();

      const localDbs = idbDatabases
        .map((db) => db.name)
        .filter((name): name is string => !!name && name.startsWith('_pouch_sector_'))
        .map((name) => name.replace('_pouch_', ''));

      const dbsToDelete = localDbs.filter((localDb) => !apiDatabases.includes(localDb));

      for (const dbName of dbsToDelete) {
        console.log(`Purger la base locale obsolète : ${dbName}`);

        const dbInstance = new PouchDB(dbName);
        await dbInstance.destroy();

        console.log(`Base ${dbName} supprimée !`);
      }
    } catch (error) {
      console.error('Erreur lors du nettoyage sans plugin :', error);
    }
  }

  mergeSectorToGlobal(sectorName: string) {
    const sectorDB = new PouchDB(sectorName);

    sectorDB.replicate
      .to(this.globalClientDb, {
        live: true,
        retry: true,
      })
      .on('change', (info) => console.log(`Synchro depuis ${sectorName} réussie`, info))
      .on('error', (err) => console.error(`Erreur de synchro pour ${sectorName}`, err));
  }
  public getDb(sectorUuid: string): PouchDB.Database | undefined {
    return this.localDatabases.get(sectorUuid);
  }
  public stopAllSync() {
    this.syncHandlers.forEach((handler) => handler.cancel());
    this.syncHandlers.clear();
    this.localDatabases.clear();
  }
}
